import React from 'react';
import Button3D from '../../ui/Button3D';
import { textSignatureProblem } from '../../../lib/signatures';
import { formatPhone } from '../../../lib/phone';

// BL-177 Step 2d (section 10): small pieces shared by Admin Center › Signatures and Settings › My signature.

/** A row of 3D option buttons where exactly one is chosen (Always on / Off; Off / Optional / Required). */
export function OptionButtons({ label, options, value, onChange, disabled = false }) {
    return (
        <div role="group" aria-label={label} className="flex flex-wrap gap-2">
            {options.map((option) => (
                <Button3D
                    key={option.value}
                    variant={option.value === value ? 'go' : 'primary'}
                    aria-pressed={option.value === value}
                    disabled={disabled}
                    onClick={() => { if (option.value !== value) onChange(option.value); }}
                >
                    {option.label}
                </Button3D>
            ))}
        </div>
    );
}

/** "TR" for The Ride: first letters of the first two words. */
export function monogram(name) {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);
    return (words.slice(0, 2).map((word) => word[0]).join('') || '?').toUpperCase();
}

function Logo({ block, size = 'h-14 w-14 text-lg' }) {
    if (block?.logo_url) {
        return <img src={block.logo_url} alt={`${block.name || 'Company'} logo`} className={`${size} flex-none rounded-xl bg-white object-contain`} />;
    }
    return (
        <span aria-hidden="true" className={`${size} inline-flex flex-none items-center justify-center rounded-xl bg-[#0B1220] font-extrabold text-[#2DD4BF]`}>
            {monogram(block?.name)}
        </span>
    );
}

/** The email company block (logo, name, phone · website) as it appears at the foot of an email. */
export function EmailCompanyBlock({ block, compact = false }) {
    if (!block || !block.name) {
        return <p className="text-sm">No company name is set in Brands &amp; Locations yet.</p>;
    }
    const contact = [formatPhone(block.phone, 'US'), block.website].filter(Boolean).join(' · ');
    return (
        <div className={`flex min-w-0 items-center gap-4 ${compact ? '' : 'rounded-2xl bg-white p-4'} text-[#0B1220]`} data-email-company-block>
            <Logo block={block} size={compact ? 'h-12 w-12 text-base' : 'h-14 w-14 text-lg'} />
            <div className="min-w-0 break-words">
                <div className={`${compact ? 'text-sm' : 'text-lg'} font-bold`}>{block.name}</div>
                {!compact && block.tagline ? <div className="text-sm">{block.tagline}</div> : null}
                {contact ? <div className="text-sm">{contact}</div> : <div className="text-sm italic">Phone and website not set</div>}
            </div>
        </div>
    );
}

/** Plain-English GSM-7 problem with the one-click fix. Renders nothing when the text is fine. */
export function TextSignatureProblem({ text, onFix }) {
    const problem = textSignatureProblem(text);
    if (!problem) return null;
    return (
        <div role="alert" className="mt-2 rounded-xl border border-orange-500 bg-[#3A1A08] px-3 py-2 text-sm text-white">
            <p>{problem.message}</p>
            <Button3D variant="pill" className="mt-2" onClick={() => onFix(problem.fixed)}>{problem.fixLabel}</Button3D>
        </div>
    );
}
